import { Outlet, Navigate } from "react-router-dom";
import { Radar, Shield, Scan, Globe } from "lucide-react";
import { useAuthStore } from "@/lib/auth";

export function AuthLayout() {
  const { isAuthenticated, isLoading } = useAuthStore();

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="h-14 w-14 rounded-xl bg-cyan-500/10 flex items-center justify-center border border-cyan-500/20">
            <Radar className="h-7 w-7 text-cyan-400 animate-pulse" />
          </div>
          <div className="animate-spin rounded-full h-6 w-6 border-2 border-cyan-500/20 border-t-cyan-500" />
        </div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="min-h-screen flex bg-background">
      {/* Left side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-gradient-to-br from-slate-950 via-cyan-950 to-slate-950">
        {/* Animated grid pattern */}
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: `
              linear-gradient(rgba(0, 255, 255, 0.05) 1px, transparent 1px),
              linear-gradient(90deg, rgba(0, 255, 255, 0.05) 1px, transparent 1px)
            `,
            backgroundSize: '50px 50px',
          }}
        />

        {/* Scan line effect */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div
            className="absolute left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-400/60 to-transparent"
            style={{
              animation: 'scanDown 4s ease-in-out infinite',
            }}
          />
        </div>

        {/* Glow effects */}
        <div className="absolute top-1/3 left-1/4 w-96 h-96 bg-cyan-500/15 rounded-full blur-3xl" />
        <div className="absolute bottom-1/3 right-1/4 w-80 h-80 bg-cyan-600/10 rounded-full blur-3xl" />
        <div className="absolute top-2/3 left-1/2 w-64 h-64 bg-emerald-500/10 rounded-full blur-3xl" />

        {/* Decorative elements */}
        <div className="absolute top-20 right-20 w-32 h-32 border border-cyan-500/20 rounded-lg rotate-12 opacity-40" />
        <div className="absolute bottom-32 left-16 w-24 h-24 border border-cyan-500/20 rounded-full opacity-30" />
        <div className="absolute top-1/2 right-12 w-16 h-16 border border-cyan-500/30 rounded-md -rotate-6 opacity-50" />

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full">
          {/* Logo */}
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-cyan-400 to-cyan-600 flex items-center justify-center shadow-lg shadow-cyan-500/30">
              <Radar className="h-6 w-6 text-white" />
            </div>
            <div>
              <span className="font-bold text-xl tracking-tight">ASM Toolkit</span>
              <span className="text-cyan-300 text-xs block">Attack Surface Management</span>
            </div>
          </div>

          {/* Main content */}
          <div className="space-y-8 max-w-md">
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-cyan-300/80 text-sm">
                <Shield className="h-4 w-4" />
                <span>Secure • Fast • Comprehensive</span>
              </div>
              <h1 className="text-5xl font-bold leading-tight">
                Discover Your
                <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-300 to-emerald-300">
                  Attack Surface
                </span>
              </h1>
              <p className="text-cyan-100/70 text-lg leading-relaxed">
                Continuously monitor your digital footprint. Identify vulnerabilities before attackers do.
              </p>
            </div>

            {/* Feature highlights */}
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center gap-3 p-3 rounded-lg bg-white/5 border border-white/10">
                <div className="p-2 rounded-md bg-cyan-500/20">
                  <Scan className="h-4 w-4 text-cyan-400" />
                </div>
                <div>
                  <p className="text-sm font-medium">12+ Modules</p>
                  <p className="text-xs text-cyan-300/60">Comprehensive scanning</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 rounded-lg bg-white/5 border border-white/10">
                <div className="p-2 rounded-md bg-emerald-500/20">
                  <Globe className="h-4 w-4 text-emerald-400" />
                </div>
                <div>
                  <p className="text-sm font-medium">Real-time</p>
                  <p className="text-xs text-cyan-300/60">Continuous monitoring</p>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between text-sm text-cyan-300/50">
            <span>© {new Date().getFullYear()} Sertua</span>
            <div className="flex items-center gap-2">
              <div className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
              <span>All systems operational</span>
            </div>
          </div>
        </div>

        {/* CSS for scan animation */}
        <style>{`
          @keyframes scanDown {
            0%, 100% { top: -2px; opacity: 0; }
            10% { opacity: 1; }
            90% { opacity: 1; }
            100% { top: 100%; opacity: 0; }
          }
        `}</style>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12 relative">
        {/* Subtle background pattern for light mode */}
        <div className="absolute inset-0 opacity-[0.02] dark:opacity-0 pointer-events-none"
          style={{
            backgroundImage: `radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0)`,
            backgroundSize: '24px 24px',
          }}
        />

        <div className="w-full max-w-md relative z-10">
          {/* Mobile logo - hidden on desktop since we have the left panel */}
          <div className="flex flex-col items-center mb-8 lg:hidden">
            <div className="h-14 w-14 rounded-xl bg-gradient-to-br from-cyan-400 to-cyan-600 flex items-center justify-center shadow-lg shadow-cyan-500/20 mb-4">
              <Radar className="h-7 w-7 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-foreground">ASM Toolkit</h1>
            <p className="text-muted-foreground text-sm">Attack Surface Management</p>
          </div>

          <Outlet />
        </div>
      </div>
    </div>
  );
}
